import React, { useEffect, useRef, useState } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { AUTH_TOKEN, ORG_ID, DOMAIN_ID, REACT_APP_API } from "../../../constants";

// ANSI color codes
const colors = {
  reset: "\x1b[0m",
  yellow: "\x1b[33m",
  green: "\x1b[32m",
  red: "\x1b[31m",
  cyan: "\x1b[36m",
};

// Define the specific string that indicates the session ended via 'exit' command
const EXIT_INDICATOR_STRING = "Terminal read error"; // Based on your description

// Add onTerminalExit to the props
function MySqlTerminal({ connectionString, onTerminalExit }) {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const socketRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState("initializing");

  // Function to send terminal size to server
  const sendSize = (socket) => {
    if (socket && socket.readyState === WebSocket.OPEN && xtermRef.current) {
      socket.send(
        JSON.stringify({
          type: "resize",
          data: {
            rows: xtermRef.current.rows,
            cols: xtermRef.current.cols,
          },
        })
      );
    }
  };

  useEffect(() => {
    // Initialize xterm with custom styling
    const term = new Terminal({
      cursorBlink: true,
      macOptionIsMeta: true,
      scrollback: 1000,
      theme: {
        background: "#000",
        foreground: "#f0f0f0",
      },
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      padding: 8,
    });
    xtermRef.current = term;

    // Initialize fit addon
    const fitAddon = new FitAddon();
    fitAddonRef.current = fitAddon;
    term.loadAddon(fitAddon);

    if (terminalRef.current) {
      term.open(terminalRef.current);
      term.write(`\r\n${colors.yellow}Initializing secure connection...${colors.reset}\r\n`);
      setTimeout(() => fitAddon.fit(), 50);
    }

    // Step 1: Send initial command to server
    const initializeTerminal = async () => {
      try {
        setConnectionStatus("initializing");
        term.write(`\r\n${colors.yellow}Requesting terminal session...${colors.reset}\r\n`);
        
        const initUrl = `${REACT_APP_API}/console/init`;
        const response = await fetch(initUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            command: connectionString,
            org_id: ORG_ID(),
            tenant_id: DOMAIN_ID(),
            sessionId: AUTH_TOKEN(), // Using auth token as session ID
          }),
        });
        console.log("Init Response:", response);

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `Failed to initialize terminal session (HTTP ${response.status}). ${errorBody}`
          );
        }

        setConnectionStatus("authenticating");
        term.write(
          `\r\n${colors.yellow}Session initialized. Establishing secure WebSocket connection...${colors.reset}`
        );
        term.write(
          `\r\n${colors.yellow}Please check your Authnull authenticator app for approval if required.${colors.reset}\r\n`
        );

        // Step 2: Connect to WebSocket after successful initialization

        const socket = new WebSocket(
          `${REACT_APP_API}`.replace(/^https/, 'wss') + `/console/ws?sessionId=` + encodeURIComponent(AUTH_TOKEN())
        );
        socketRef.current = socket;

        socket.onopen = () => {
          setConnectionStatus("connected");
          term.write("\r\n\x1b[2J\x1b[H"); // Clear screen
          term.write(`\r\n${colors.green}Connection successful!${colors.reset}\r\n`);
          term.write(
            `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
          );
          term.write(
            `                 ${colors.cyan}authnull${colors.reset}                       \r\n`
          );
          term.write(
            `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
          );
          term.write("\r\n");
          sendSize(socket);
        };

        socket.onmessage = (event) => {
          let isExitMessage = false; // Flag to check if it's the exit message

          try {
            const message = JSON.parse(event.data);
            if (message.type === "data") {
              term.write(message.data);
            } else if (message.type === "error") {
              const errorMessage = message.data || ""; // Ensure message.data is a string
              console.error("Terminal error:", errorMessage);
              term.write(`\r\n${colors.red}Error from server: ${errorMessage}${colors.reset}\r\n`);
              // **** Check if this error message indicates the user exited ****
              if (
                typeof errorMessage === "string" &&
                errorMessage.includes(EXIT_INDICATOR_STRING)
              ) {
                isExitMessage = true;
                term.write(
                  `\r\n${colors.yellow}Detected exit command. Closing terminal...${colors.reset}\r\n`
                );
              }
            }
          } catch (e) {
            // Fallback for non-JSON messages
            if (typeof event.data === "string") {
              term.write(event.data);
              // **** Check if this raw message indicates the user exited ****
              if (event.data.includes(EXIT_INDICATOR_STRING)) {
                isExitMessage = true;
                // Avoid writing the raw error again if we already identified it
                // Write a user-friendly closing message instead
                term.write(
                  `\r\n${colors.yellow}Detected exit command. Closing terminal...${colors.reset}\r\n`
                );
              }
            } else {
              console.warn("Received non-string WebSocket message:", event.data);
            }
          }

          // **** If the exit message was detected, call the callback function ****
          if (isExitMessage && typeof onTerminalExit === "function") {
            // Optionally add a small delay so the user can see the message
            setTimeout(() => {
              onTerminalExit();
            }, 1500); // 1.5 second delay
          }
        };

        socket.onclose = (event) => {
          setConnectionStatus("disconnected");
          let reason = event.reason ? ` Reason: ${event.reason}` : "";
          if (!event.wasClean) {
            term.write(
              `\r\n${colors.red}Connection closed unexpectedly (Code: ${event.code}).${reason}${colors.reset}`
            );
            term.write(
              `\r\n${colors.yellow}Please refresh the page to try again.${colors.reset}\r\n`
            );
          } else {
            // Avoid showing generic 'Connection closed' if we already handled the exit case
            // Check if the status is already something else before writing this
            if (connectionStatus !== "exiting") {
              // You might need a state for this or check a ref flag
              term.write(
                `\r\n${colors.yellow}Connection closed (Code: ${event.code}).${reason}${colors.reset}\r\n`
              );
            }
          }
          // If the connection closes unexpectedly *before* exit is detected,
          // ensure the parent is notified if needed (though manual close is the primary mechanism here)
          // if (typeof onTerminalExit === 'function') { onTerminalExit(); } // Potentially add this if needed for abrupt closes
        };

        socket.onerror = (error) => {
          setConnectionStatus("error");
          console.error("WebSocket error:", error);
          term.write(`\r\n${colors.red}WebSocket connection error.${colors.reset}`);
          term.write(`\r\n${colors.yellow}Debugging Tips:${colors.reset}`);
          term.write(`\r\n  - Check your internet connection.`);
          term.write(
            `\r\n  - Verify firewall/proxy settings are not blocking WebSockets (wss://).`
          );
          term.write(`\r\n  - Ensure your Authnull session is still valid.`);
          term.write(`\r\n  - Consider refreshing the window.${colors.reset}\r\n`);
        };

        // Terminal input handling
        term.onData((data) => {
          if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "data", data }));
          } else {
            console.warn("WebSocket not open. Cannot send data.");
          }
        });
      } catch (error) {
        setConnectionStatus("error");
        console.error("Failed to setup terminal:", error);
        term.write(`\r\n${colors.red}Failed to setup terminal: ${error.message}${colors.reset}`);
        term.write(`\r\n${colors.yellow}Debugging Tips:${colors.reset}`);
        term.write(`\r\n  - Check your internet connection.`);
        term.write(`\r\n  - Verify the Authnull API endpoint is reachable.`);
        term.write(`\r\n  - Ensure your Authnull policy allows this connection.`);
        term.write(`\r\n  - Check the browser console (F12) for more detailed errors.`);
        term.write(`\r\n  - Consider refreshing the window.${colors.reset}\r\n`);
      }
    };

    initializeTerminal();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (fitAddonRef.current && xtermRef.current) {
        fitAddonRef.current.fit();
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          sendSize(socketRef.current);
        }
      }
    });

    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
      resizeObserverRef.current = resizeObserver;
    }

    // Cleanup
    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
      if (socketRef.current) {
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
        xtermRef.current = null;
      }
      fitAddonRef.current = null;
      terminalRef.current = null;
    };
    // Add onTerminalExit to dependency array if it could change, though unlikely for a function ref
  }, [connectionString, onTerminalExit]);

  return (
    <div
      ref={terminalRef}
      style={{
        width: "100%",
        height: "100%",
        background: "#000",
        borderRadius: "4px",
        overflow: "hidden",
      }}
      className="mysql-terminal-container"
    >
      <style jsx>{`
        .mysql-terminal-container {
          position: relative;
        }
        .mysql-terminal-container :global(.xterm-viewport::-webkit-scrollbar) {
          width: 10px;
        }
        .mysql-terminal-container :global(.xterm-viewport::-webkit-scrollbar-track) {
          background: #0a0a0a;
        }
        .mysql-terminal-container :global(.xterm-viewport::-webkit-scrollbar-thumb) {
          background: #444;
          border-radius: 5px;
        }
      `}</style>
    </div>
  );
}

export default MySqlTerminal;
